/**
 * QR-1b: публичный лендинг приглашения `/j/#<token>` (тикет QR-1 §4, §3.3).
 *
 * Это страница ДЛЯ ПРИГЛАШЁННОГО, не для владельца: маршрут вне PrivateRoutes,
 * сессии ЛК здесь нет и быть не должно:
 *  - токен читается ТОЛЬКО из фрагмента (location.hash), query не читается
 *    никогда (зеркало Android InviteLink); сразу после чтения
 *    history.replaceState(null, '', '/j/') — токен исчезает из адресной
 *    строки/истории (остаётся в памяти компонента);
 *  - preview идёт через ОТДЕЛЬНЫЙ axios без интерсепторов ЛК (publicApi):
 *    протухший access_token владельца в том же браузере не должен
 *    редиректить лендинг на форму логина (High-1 дизайн-ревью);
 *  - AuthProvider на '/j/' mount-вызов /me пропускает (isPublicPath);
 *  - кнопка «Открыть в приложении» — Chrome intent-URL с токеном в extra
 *    `invite_token` (разбирается локально, сетевого запроса нет), fallback —
 *    этот же лендинг С ТЕМ ЖЕ токеном во фрагменте (приложения нет -> человек
 *    возвращается на живое приглашение, а не в тупик); «Установить» — только
 *    если сервер отдал install_url (ФСБ-гейт §6: пусто до ответа юриста);
 *  - ПДн-уведомление показывается ДО входа (§3.10): сервер отдаёт
 *    privacy_version/privacy_policy_url, приложение шлёт версию в join;
 *  - inline-скриптов нет (CSP §7), токен в логи/DOM-текст не пишется
 *    (только в href intent-ссылки — это и есть контрактный носитель).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinInvite, previewInvite, refreshMemberToken } from '../../msg/publicApi';
import DeviceImportPanel from '../../msg/deviceImportPanel';
import {
  intentUrl, inviteUrl, isAndroidUa, LANDING_PATH, MEMBER_CHAT_PATH, tokenFromHash,
  tokenFromPasted,
} from '../../msg/inviteLink';
import { browserDeviceId } from '../../msg/deviceId';
import { setArrivalIntent } from '../../msg/arrivalIntent';
import {
  clearMemberSession, loadMemberSession, saveMemberSession, sessionFromJoin,
} from '../../msg/memberSession';
import './joinLanding.css';

/** Чтение токена из фрагмента + немедленная зачистка адресной строки.
 *  Вынесено: пинится тестом без рендера. */
export function takeTokenFromLocation(loc = window.location, hist = window.history) {
  const token = tokenFromHash(loc.hash);
  if (loc.hash) {
    // Даже битый фрагмент убираем: в истории/адресной строке ему не место
    hist.replaceState(null, '', LANDING_PATH);
  }
  return token;
}

const JoinLanding = () => {
  // Токен берётся на mount (lazy init): после replaceState в location.hash
  // его уже нет — повторный рендер ничего не перечитает. Плюс hashchange:
  // вставка ссылки с фрагментом в адресную строку УЖЕ открытой вкладки /j/ —
  // same-document навигация без перезагрузки (живой прогон 23.08) — тоже
  // должна подхватить токен, а не оставить «недействительно».
  const [token, setToken] = useState(() => takeTokenFromLocation());
  // WEB-29: то, что человек вставил руками, и признак «не разобрали».
  const [pasted, setPasted] = useState('');
  const [pasteBad, setPasteBad] = useState(false);
  // loading|ok|notoken|invalid|error.
  // 🔴 notoken и invalid РАЗНЫЕ (живой прогон 25.08): «устарела или была
  // отозвана» — приговор приглашению, и человек идёт просить новое. Но чаще
  // токена просто нет в адресе: повторное открытие /j/, возврат назад,
  // fallback из intent-ссылки. Приглашение при этом живое, нужно лишь
  // открыть ссылку заново — врать про отзыв нельзя.
  const [status, setStatus] = useState(token ? 'loading' : 'notoken');
  const [preview, setPreview] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const onHash = () => {
      const hadHash = Boolean(window.location.hash);
      const next = takeTokenFromLocation();
      // Ревью р.2, Medium: старая карточка не должна пережить новый фрагмент —
      // ни валидный (до ответа preview показывали бы label A с intent B), ни
      // битый (intent со старым токеном остался бы навсегда). Пустой hash
      // (replaceState свой hashchange не шлёт; это, например, back) — не событие.
      if (next) {
        setPreview(null);
        setStatus('loading');
        setToken(next);
        setAttempt((n) => n + 1);
      } else if (hadHash) {
        setPreview(null);
        setToken(null);
        // Битый фрагмент — тоже НЕ «отозвано»: чаще всего ссылку обрезали при
        // пересылке. Действие человека то же самое — открыть её заново из QR.
        setStatus('notoken');
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const data = await previewInvite(token);
        if (cancelled) return;
        setPreview(data);
        setStatus('ok');
      } catch (e) {
        if (cancelled) return;
        // 404 — неизвестный/отозванный/кривой, неразличимо (§3.4); остальное —
        // сеть/5xx, можно повторить
        setStatus(e?.status === 404 ? 'invalid' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, attempt]);

  const host = window.location.host;
  // 🔴 fallback intent-ссылки НЕСЁТ ТОКЕН (живой прогон 25.08). Раньше это был
  // голый `/j/`, и человек без установленного приложения попадал на лендинг
  // БЕЗ приглашения — первое ложное «недействительно» на бою пришло именно
  // отсюда. Токен и так лежит в этой же intent-ссылке (S.invite_token), терять
  // его на ровном месте незачем: экспозиция та же, что у самого QR — Chrome
  // разбирает intent локально, а фрагмент браузер серверу не шлёт (RFC 7231).
  /**
   * WEB-29: пустить вставленное по обычному пути входа.
   *
   * 🔴 Кладём токен во фрагмент адреса, а не в состояние напрямую: тогда
   * дальше работает ровно та же дорога, что и по нажатой ссылке, — со всеми
   * её проверками, отказами и текстами. Своя параллельная дорога разошлась бы
   * с основной на первой же правке.
   */
  const openPasted = () => {
    const t = tokenFromPasted(pasted);
    if (!t) { setPasteBad(true); return; }
    setPasteBad(false);
    // 🔴 В адрес токен НЕ пишем (ревью 10.09). Запись в hash поднимала
    // `hashchange`, тот увеличивал `attempt` и перезапускал эффект — проверка
    // приглашения уходила на сервер ДВАЖДЫ. А `preview` делит лимит по IP с
    // самим входом (20r/m, burst 30): у коллег за одним адресом второй запрос
    // мог вернуть 429, и человек видел «не удалось проверить приглашение»,
    // хотя первый запрос приглашение уже проверил.
    // ⭐ Заодно так лучше: токен больше не попадает в историю браузера.
    setToken(t);
    setStatus('loading');
  };

  const fallbackUrl = token ? inviteUrl(window.location.origin, token) : null;
  const android = isAndroidUa(navigator.userAgent);

  // QR-3: вход прямо в браузере — единственный путь для iPhone и любого
  // устройства без нашего приложения.
  const [name, setName] = useState('');
  // idle|form|joining|failed|taken|switch («switch» — подтверждение замены
  // участника на этом устройстве)
  const [joinState, setJoinState] = useState('idle');
  const [joinError, setJoinError] = useState(null);
  // 🔴 join_key — ключ ПОПЫТКИ, а не запроса: сетевой ретрай обязан слать тот
  // же, иначе сервер в окне реплея заведёт ВТОРОГО участника и съест место в
  // потолке приглашения. Поэтому ref, а не значение в обработчике.
  const joinKeyRef = useRef(null);
  // 🔴 WEB-19 §4.1 (Д-2): прежний member-JWT этого браузера — доказательство,
  // что «войти по этой ссылке ещё раз» делает ТОТ ЖЕ человек. Сервер продолжает
  // прежнего участника только по нему: did не секрет внутри комнаты (ревью
  // р.1, Medium). Берётся из живой сессии ДО того, как замена её стирает.
  const priorTokenRef = useRef(null);
  // 🔴 Хранилище участника — ОДНО на браузер (`msg_member_session`, см.
  // memberSession): второй вход затирает первый. Раньше это было ещё и молча
  // недоступно — при живой сессии кнопки входа просто не было, и человек,
  // которому прислали ВТОРОЕ приглашение на тот же телефон, упирался в
  // «Вернуться в чат» с чужим собеседником и не понимал, почему не пускает
  // (ревью Codex р.3). Теперь замена — явное действие с предупреждением.
  const [existing, setExisting] = useState(() => loadMemberSession());
  const alreadyIn = Boolean(existing);
  // Д-3: приём файла с прежнего устройства. Только когда сессии ЕЩЁ НЕТ:
  // хранилище участника одно на браузер, и импорт поверх живого чата
  // затёр бы того, кто в нём сидит.
  const [importOpen, setImportOpen] = useState(false);

  /**
   * 🔴 Переход В ПРЕДЕЛАХ приложения, а НЕ `window.location.assign`.
   *
   * Полная навигация уничтожает контекст JS — вместе с ним и запасное хранение
   * сессии в памяти модуля. А оно единственное, что есть у человека в приватном
   * режиме Safari и при «блокировать данные сайта»: `saveMemberSession` там
   * возвращает false, в localStorage не ложится ничего. Получалось так:
   * приглашение потрачено, сервер выдал member-JWT, человек нажал «Открыть
   * чат» — и попал на «вы ещё не вошли в чат», а второго входа по той же
   * ссылке может не быть. Дальше он открывает приглашение заново и становится
   * ВТОРЫМ участником — та самая механика дублей (тикет WEB-19).
   */
  const navigate = useNavigate();
  const goToChat = () => { navigate(MEMBER_CHAT_PATH); };
  // WEB-19 §4.3 (Д-2): человек пришёл менять подпись — в чат с этим намерением,
  // форма там откроется сама (правка подписи живёт в чате, у живого транспорта).
  const goRename = () => { setArrivalIntent({ rename: true }); goToChat(); };

  /** Замена участника на этом устройстве — только по явному подтверждению. */
  const switchMember = () => {
    priorTokenRef.current = existing?.token ?? null;
    clearMemberSession();
    setExisting(null);
    setJoinState('form');
  };

  const doJoin = async () => {
    const displayName = name.trim();
    if (!displayName) return;
    if (!joinKeyRef.current) joinKeyRef.current = globalThis.crypto.randomUUID();
    setJoinState('joining');
    setJoinError(null);
    try {
      const deviceId = browserDeviceId();
      const out = await joinInvite({
        token,
        displayName,
        deviceId,
        joinKey: joinKeyRef.current,
        privacyVersion: preview.privacy_version,
        resumeToken: priorTokenRef.current,
      });
      // WEB-19 §4.1 (Д-2): сервер мог ПРОДОЛЖИТЬ прежнего участника этого
      // устройства, а не завести нового — тогда подпись берём его ТЕКУЩУЮ
      // (`display_name`), а не набранную сейчас: иначе подвал чата показывал бы
      // имя, которого на сервере нет. Набранное имя человек применит явно, в
      // чате — оно едет туда намерением, а не пропадает молча.
      const serverName = out.resumed ? (out.display_name ?? displayName) : displayName;
      saveMemberSession(sessionFromJoin(out, deviceId, serverName));
      if (out.resumed) {
        setArrivalIntent({
          resumed: true,
          typedName: displayName !== serverName ? displayName : null,
        });
      }
      goToChat();
    } catch (e) {
      // 409 — приглашение полное/условия сменились, 404 — отозвано: это
      // терминально, повтор тем же ключом не поможет и место не освободит.
      const terminal = [404, 409].includes(e?.status);
      setJoinState(terminal ? 'taken' : 'failed');
      setJoinError(terminal
        ? 'Приглашение больше не действует или в чате нет свободного места. Попросите новый QR-код.'
        : 'Не удалось войти — проверьте связь и попробуйте ещё раз.');
    }
  };

  const body = () => {
    // ok без preview не бывает (сброс идёт парой), но рендер ok привязан к
    // наличию данных — карточка никогда не рисуется «по старому статусу»
    if (status === 'loading' || (status === 'ok' && !preview)) {
      return <p className="joinNote">Проверяем приглашение…</p>;
    }
    if (status === 'notoken') {
      return (
        <>
          <h1 className="joinTitle">Нужна ссылка-приглашение</h1>
          <p className="joinNote">
            В адресе нет приглашения. Отсканируйте QR-код заново или откройте
            присланную ссылку целиком — при пересылке её часто обрезают.
          </p>
          {/* Сюда же приводит «назад» и повторное открытие /j/ у того, кто уже
              вошёл: у него чат на месте, и упирать его в тупик незачем. */}
          {alreadyIn && (
            <>
              <button
                type="button"
                className="joinBtn joinPrimary"
                data-testid="join-back-to-chat"
                onClick={goToChat}
              >
                Вернуться в чат
              </button>
              <button
                type="button"
                className="joinBtn"
                data-testid="join-rename"
                onClick={goRename}
              >
                Изменить подпись
              </button>
            </>
          )}
          {/* 🔴 WEB-29: поле для вставки. У приложения с экрана «Домой» на iOS
              своё хранилище, отдельное от Safari, — сессия, рождённая при
              открытии приглашения в Safari, в приложение НЕ попадает. Значит
              войти внутри приложения можно только так: вставить ссылку руками.
              Один раз — дальше значок будет открывать переписку сам. */}
          {!alreadyIn && (
            <div className="joinPasteRow">
              <label className="joinNameLabel" htmlFor="join-paste">
                Вас пригласили ВПЕРВЫЕ? Вставьте ссылку из приглашения
              </label>
              {/* 🔴 Ловушка, которую тут легко устроить: у человека, который
                  УЖЕ в чате на другом устройстве, приложение пустое, и он
                  вставит сюда своё старое приглашение. Появится второй он, а
                  переписка останется на прежнем устройстве. Предупреждение
                  стоит ДО поля, а не после: после его не читают. */}
              <p className="joinNote" data-testid="join-paste-warn">
                Если чат у вас уже открыт на телефоне или на компьютере — не
                вставляйте приглашение сюда: появится второй вы, а переписка
                останется там. Вам нужен «Перенести чат с другого устройства»
                ниже.
              </p>
              <input
                id="join-paste"
                className="joinNameInput"
                data-testid="join-paste"
                type="text"
                value={pasted}
                placeholder="https://…/j/#…"
                onChange={(e) => { setPasted(e.target.value); setPasteBad(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') openPasted(); }}
              />
              <button
                type="button"
                className="joinBtn joinPrimary"
                data-testid="join-paste-go"
                onClick={openPasted}
              >
                Открыть чат
              </button>
              {pasteBad && (
                <p className="joinNote" data-testid="join-paste-bad">
                  Не похоже на приглашение. Скопируйте ссылку целиком — при
                  пересылке её часто обрезают. Можно вставить и один только код
                  из её конца.
                </p>
              )}
            </div>
          )}
          {/* 🔴 WEB-28. Второй тупик, в который упёрся владелец: ярлык вёл на
              «вы ещё не вошли в чат», оттуда — сюда, а здесь не было ВООБЩЕ
              ничего, кроме «нужна ссылка». При этом перенос с другого
              устройства работает БЕЗ приглашения и дубля не плодит — путь был,
              его просто не показывали именно там, где человек застревал. */}
          {!alreadyIn && !importOpen && (
            <button
              type="button"
              className="joinBtn"
              data-testid="join-import-notoken"
              onClick={() => setImportOpen(true)}
            >
              Перенести чат с другого устройства
            </button>
          )}
          {!alreadyIn && importOpen && (
            <DeviceImportPanel
              onCheck={refreshMemberToken}
              onDone={goToChat}
              onClose={() => setImportOpen(false)}
            />
          )}
        </>
      );
    }
    if (status === 'invalid') {
      return (
        <>
          <h1 className="joinTitle">Приглашение недействительно</h1>
          <p className="joinNote">
            Ссылка устарела или была отозвана. Попросите у пригласившего новый QR-код.
          </p>
        </>
      );
    }
    if (status === 'error') {
      return (
        <>
          <h1 className="joinTitle">Не удалось проверить приглашение</h1>
          <p className="joinNote">Сеть недоступна или сервер не отвечает. Попробуйте ещё раз.</p>
          <button type="button" className="joinBtn" onClick={() => setAttempt((n) => n + 1)}>
            Повторить
          </button>
        </>
      );
    }
    return (
      <>
        <p className="joinLead">Вас приглашает</p>
        <h1 className="joinTitle" data-testid="join-label">{preview.label}</h1>
        {/* Название продукта уже стоит в фирменной строке — здесь только суть */}
        <p className="joinNote">
          Сообщения шифруются сквозным шифрованием: читаете их только вы и
          пригласивший.
        </p>
        <div className="joinActions">
          {alreadyIn && (
            <>
              <button
                type="button"
                className="joinBtn joinPrimary"
                data-testid="join-back-to-chat"
                onClick={goToChat}
              >
                Вернуться в чат
              </button>
              {/* WEB-19 §4.3 (Д-2): наблюдение 09.09 — человек шёл менять имя, а
                  находил только «войти заново» и становился дублем. */}
              <button
                type="button"
                className="joinBtn"
                data-testid="join-rename"
                onClick={goRename}
              >
                Изменить подпись
              </button>
              {joinState !== 'switch' && (
                <button
                  type="button"
                  className="joinBtn"
                  data-testid="join-switch"
                  onClick={() => setJoinState('switch')}
                >
                  Войти по этой ссылке как другой участник
                </button>
              )}
              {joinState === 'switch' && (
                <div className="joinSwitchWarn" data-testid="join-switch-warn">
                  <p className="joinNote">
                    На этом устройстве уже открыт чат
                    {existing?.peer?.name ? ` с ${existing.peer.name}` : ''}. Браузер
                    держит только один чат участника: вход по новой ссылке закроет
                    прежний здесь, и вернуться в него будет нечем — ссылка
                    одноразовая. Сама переписка останется у собеседника.
                  </p>
                  <button
                    type="button"
                    className="joinBtn"
                    data-testid="join-switch-confirm"
                    onClick={switchMember}
                  >
                    Всё равно войти по новой ссылке
                  </button>
                  <button
                    type="button"
                    className="joinBtn"
                    data-testid="join-switch-cancel"
                    onClick={() => setJoinState('idle')}
                  >
                    Отмена
                  </button>
                </div>
              )}
            </>
          )}
          {!alreadyIn && joinState !== 'form' && joinState !== 'joining' && (
            <button
              type="button"
              className="joinBtn joinPrimary"
              data-testid="join-in-browser"
              onClick={() => setJoinState('form')}
            >
              Открыть чат в браузере
            </button>
          )}
          {/* Д-3: второй вход — для того, у кого переписка УЖЕ есть, но на
              другом устройстве. Кнопка вторичная: по этой ссылке чаще приходят
              впервые, и главный путь остаётся главным. */}
          {!alreadyIn && !importOpen && joinState !== 'form' && joinState !== 'joining' && (
            <button
              type="button"
              className="joinBtn"
              data-testid="join-import"
              onClick={() => setImportOpen(true)}
            >
              Перенести чат с другого устройства
            </button>
          )}
          {!alreadyIn && importOpen && (
            <DeviceImportPanel
              onCheck={refreshMemberToken}
              onDone={goToChat}
              onClose={() => setImportOpen(false)}
            />
          )}
          {!alreadyIn && (joinState === 'form' || joinState === 'joining') && (
            <div className="joinNameRow">
              <label className="joinNameLabel" htmlFor="join-name">
                Как вас подписать в чате?
              </label>
              <input
                id="join-name"
                className="joinNameInput"
                type="text"
                value={name}
                maxLength={64}
                placeholder="Имя"
                autoComplete="name"
                disabled={joinState === 'joining'}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doJoin(); }}
              />
              <button
                type="button"
                className="joinBtn joinPrimary"
                data-testid="join-submit"
                disabled={joinState === 'joining' || !name.trim()}
                onClick={doJoin}
              >
                {joinState === 'joining' ? 'Входим…' : 'Войти в чат'}
              </button>
            </div>
          )}
          {joinError && (
            <p className="joinNote joinError" data-testid="join-error">{joinError}</p>
          )}
          <a
            className="joinBtn"
            href={intentUrl({ host, token, fallbackUrl })}
            data-testid="join-open-app"
          >
            Открыть в приложении
          </a>
          {preview.install_url && (
            <a
              className="joinBtn"
              href={preview.install_url}
              rel="noreferrer"
              data-testid="join-install"
            >
              Установить приложение
            </a>
          )}
        </div>
        <p className="joinHint">
          Чат работает прямо в браузере — на iPhone, Android и компьютере,
          устанавливать ничего не нужно.
          {android && ' Приложение Total даёт то же самое с уведомлениями.'}
        </p>
        <details className="joinPrivacy" data-testid="join-privacy">
          <summary>Персональные данные</summary>
          <p>
            При входе приложение передаст пригласившему указанное вами имя и
            создаст на сервере учётную запись участника чата. Пригласивший может
            удалить её в любой момент; вы можете покинуть чат сами. Версия условий:{' '}
            <code>{preview.privacy_version}</code>.
            {preview.privacy_policy_url && (
              <>
                {' '}
                <a href={preview.privacy_policy_url} rel="noreferrer">Политика обработки данных</a>
              </>
            )}
          </p>
        </details>
      </>
    );
  };

  return (
    <main className="JoinLanding">
      <div className="joinCard">
        {/* Человек открывает ссылку с чужого телефона и должен сразу понимать,
            что это за страница. Замок — не украшение: E2E тут главное. */}
        <div className="joinBrand">
          <span className="joinBrandMark" aria-hidden="true">🔒</span>
          <span>
            <span className="joinBrandName">Total</span>
            {' '}
            <span className="joinBrandSub">защищённый чат</span>
          </span>
        </div>
        {body()}
      </div>
    </main>
  );
};

export default JoinLanding;
